import { createFileRoute } from "@tanstack/react-router";
import { Mail, Smartphone } from "lucide-react";
import { cloneElement, isValidElement, useId, useState } from "react";
import { PillBtn } from "../components/primitives.js";
import { Input, Textarea } from "../components/ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "../components/ui/select.js";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { createHappyFoxTicket } from "#/server/functions/happyfox.js";
import { notify } from "#/lib/notify.js";

export const Route = createFileRoute("/contact")({
  head: () => ({ meta: [{ title: "Contact" }] }),
  component: ContactPage,
});

const CONCERN_TYPES_OBJECT = {
  "General Inquiry": 63,
  "Order Status": 75,
  "Book Returns": 77,
  "Book Refund" : 78,
  "Shipping": 73,
  "Product Question": 63,
  "Billing": 65,
  "Partnership": 63,
  "Other": 63,
};

const CONCERN_TYPES = [
  "General Inquiry",
  "Order Status",
  "Book Returns",
  "Book Refund",
  "Shipping",
  "Product Question",
  "Billing",
  "Partnership",
  "Other",
 ] as const;

const ContactFormSchema = z.object({
  concern: z.string().min(1), 
  subject: z.string().min(1),
  message: z.string().min(1),
  fullName: z.string().min(1),
  cc: z.string(),
  bcc: z.string(),
  attachment: z.instanceof(File).nullable(),
  email: z.email().min(1),
  phone: z.string().min(1),
});
type contactFormType = z.infer<typeof ContactFormSchema>;

const defaultContactForm = {
  concern: "",
  subject: "",
  message: "",
  fullName: "",
  cc: "",
  bcc: "",
  attachment: null as File | null,
  email: "",
  phone: "",
}

const FIELD_CLASS = "h-14 px-6";
function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [isCCActive, setIsCCActive] = useState(false);
  const [isBCCActive, setIsBCCActive] = useState(false);
  const [isAttachmentActive, setIsAttachmentActive] = useState(false);

  const form = useForm({
    defaultValues: defaultContactForm,
    validators: {onChange: ContactFormSchema},
    onSubmit: ({ value }) => {
      return mutation.mutateAsync(value);
    },
  })

  const mutation = useMutation({
    mutationFn: (values: contactFormType) => {
      const {
        subject, 
        concern, 
        fullName, 
        email, 
        cc, 
        message
      } = values
      
      const ccList = cc ? cc.split(" ") : [];
      const payload = {
        subject: subject,
        text: message,
        email: email,
        name: fullName,
        cc: ccList,
        category: 8, //Learning Plus ID
        priority: 4, 
        "t-cf-7": CONCERN_TYPES_OBJECT[concern as keyof typeof CONCERN_TYPES_OBJECT]
      }
      return createHappyFoxTicket({data: payload})

    }, 
    onSuccess: () => {
      notify.success("The support ticket is created succesfully.")
    }, 
    onError: (err) => {
      console.log(err)
      notify.error("Unable to create a support ticket", {
          description: err instanceof Error ? err.message : undefined,
      })
    }, 
  })



  return (
    <div data-screen-label="Contact" className="pb-24">
      <section className="mx-auto w-full max-w-figma px-6 pt-12 md:px-10 md:pt-16">
        <h1 className="m-0 font-bold font-display text-5xl md:text-6xl text-lp-green-dk leading-none tracking-tight">
          GOT QUESTIONS?
        </h1>
        <p className="mt-4 m-0 font-display font-medium text-lg md:text-xl text-lp-green-mid">
          Ask and we&rsquo;ll respond as soon as we can!
        </p>
      </section>
      
      <section className="mx-auto w-full max-w-figma px-6 mt-10 md:px-10 md:mt-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] lg:gap-12">
          <form 
            onSubmit={(e) => {
              e.preventDefault();
              setSubmitted(true);
              form.handleSubmit();
            }}
            className="flex flex-col gap-5"
          >
            <h2 className="m-0 font-display font-semibold text-2xl text-lp-green-dk">
              LP Ticketing
            </h2>

            <div className="mt-2 h-px w-full bg-lp-green-dk/40" />

            <form.Field name="concern">
              {(field) => (
                <Field label="Type of Concern" required>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) => field.handleChange(v ?? "")}
                    required
                  >
                    <SelectTrigger className="h-14 px-6" placeholder=" " />
                    <SelectContent>
                      {CONCERN_TYPES.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </form.Field>

            <form.Field name="subject">
              {(field) => (
                <Field label="Subject" required>
                  <Input
                    type="text"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    required
                    className={FIELD_CLASS}
                  />
                </Field>
              )}
            </form.Field>

            <form.Field name="message">
              {(field) => (
                <Field label="Message" required>
                  <Textarea
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    required
                    rows={9}
                    className="rounded-3xl px-6 py-4"
                  />
                </Field>
              )}
            </form.Field>

            <div className="-mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 font-display text-sm text-lp-text-dk">
              <span aria-hidden className="text-lp-green-mid">|</span>
              <button type="button" className="cursor-pointer hover:text-lp-green-dk"
              onClick={() => {setIsCCActive(!isCCActive)}} 
              >
              { !isCCActive ? "Add CC" : "Remove CC" }
              </button>
              
              <span aria-hidden className="text-lp-green-mid">|</span>
              <button type="button" className="cursor-pointer hover:text-lp-green-dk"
              onClick={() => {setIsBCCActive(!isBCCActive)}}
              >
                { !isBCCActive ? "Add BCC" : "Remove BCC" }
              </button>
              <span aria-hidden className="text-lp-green-mid">|</span>
              <button type="button" className="cursor-pointer hover:text-lp-green-dk" 
                onClick={() => {setIsAttachmentActive(!isAttachmentActive)}}
              >
                { !isAttachmentActive ? "Attach a File" : "Remove the File" }
              </button>
            </div>
            {isCCActive &&
                <form.Field name="cc">
                  {(field) => (
                    <Field label="CC">
                      <Textarea
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        required
                        rows={9}
                        className="rounded-3xl px-6 py-4"
                      />
                    </Field>
                  )}
                </form.Field> 
              }

            {isBCCActive &&
                <form.Field name="cc">
                  {(field) => (
                    <Field label="BCC">
                      <Textarea
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        required
                        rows={9}
                        className="rounded-3xl px-6 py-4"
                      />
                    </Field>
                  )}
                </form.Field> 

              }

            { isAttachmentActive &&
              <form.Field name="attachment">
                {(field) => (
                  <Field label="Attach a File">
                    <Input
                      type="file"
                      onChange={(e) => field.handleChange(e.target.files?.[0] ?? null)}
                      onBlur={field.handleBlur}
                      className={FIELD_CLASS}
                    />
                  </Field>
                )}
              </form.Field>
            }

            <form.Field name="fullName">
              {(field) => (
                <Field label="Full Name" required>
                  <Input
                    type="text"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    required
                    className={FIELD_CLASS}
                  />
                </Field>
              )}
            </form.Field>

            <form.Field name="email">
              {(field) => (
                <Field label="Email" required>
                  <Input
                    type="email"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    required
                    className={FIELD_CLASS}
                  />
                </Field>
              )}
            </form.Field>

            <form.Field name="phone">
              {(field) => (
                <Field label="Phone" required>
                  <Input
                    type="tel"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    required
                    className={FIELD_CLASS}
                  />
                </Field>
              )}
            </form.Field>

            <div className="mt-3 flex flex-col items-end gap-3">
              {submitted && (
                <span className="font-display font-medium text-lp-green-dk">
                  Thanks &mdash; we received your ticket and will reply soon.
                </span>
              )}
              <PillBtn type="submit" variant="green" size="sm">
                Submit A Ticket
              </PillBtn>
            </div>
          </form>
          
          <aside className="self-start rounded-3xl bg-lp-green-soft p-8 lg:p-10">
            <h2 className="m-0 font-display font-semibold text-2xl text-lp-green-dk">
              Office Hours
            </h2>
            <p className="mt-3 m-0 font-display text-lp-text-dk">
              Monday to Friday 8:00am &ndash; 5:00pm
            </p>

            <h2 className="mt-8 m-0 font-display font-semibold text-2xl text-lp-green-dk">
              Contact
            </h2>
            <div className="mt-4 flex flex-col gap-3">
              <InfoRow icon={<Smartphone className="size-5" aria-hidden />}>
                <a href="tel:+63285409868" className="hover:underline">
                  Phone (+63 2 8540 9868)
                </a>
              </InfoRow>
              <InfoRow icon={<Mail className="size-5" aria-hidden />}>
                <a
                  href="mailto:support@learningplus.ph"
                  className="hover:underline"
                >
                  support@learningplus.ph
                </a>
              </InfoRow>
            </div>

            <h2 className="mt-8 m-0 font-display font-semibold text-2xl text-lp-green-dk">
              Follow Us On
            </h2>
            <div className="mt-3 flex items-center gap-3 text-lp-green-dk">
              <a
                href="https://www.facebook.com/learningplusph"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="hover:opacity-80"
              >
                <FacebookIcon />
              </a>
              <a
                href="https://www.instagram.com/learningplusph"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="hover:opacity-80"
              >
                <InstagramIcon />
              </a>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  const id = useId();
  const control = isValidElement<{ id?: string }>(children)
    ? cloneElement(children, { id })
    : children;
  return (
    <div className="block">
      <label
        htmlFor={id}
        className="mb-2 block font-display font-medium text-lp-text-dk"
      >
        {label}
        {required ? "*" : ""}
      </label>
      {control}
    </div>
  );
}

function FacebookIcon() {
  return (
    <svg
      role="img"
      aria-label="Facebook"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <title>Facebook</title>
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.92 3.78-3.92 1.1 0 2.24.2 2.24.2v2.47H15.2c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.44 2.91h-2.34V22c4.78-.76 8.43-4.92 8.43-9.94Z" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg
      role="img"
      aria-label="Instagram"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <title>Instagram</title>
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41 1.27-.06 1.65-.07 4.85-.07ZM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.34 4.14.63a5.86 5.86 0 0 0-2.13 1.38A5.86 5.86 0 0 0 .63 4.14C.34 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12c0 3.26.01 3.67.07 4.95.06 1.27.27 2.15.56 2.91a5.86 5.86 0 0 0 1.38 2.13 5.86 5.86 0 0 0 2.13 1.38c.76.29 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24c3.26 0 3.67-.01 4.95-.07 1.27-.06 2.15-.27 2.91-.56a5.86 5.86 0 0 0 2.13-1.38 5.86 5.86 0 0 0 1.38-2.13c.29-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95 0-3.26-.01-3.67-.07-4.95-.06-1.27-.27-2.15-.56-2.91a5.86 5.86 0 0 0-1.38-2.13A5.86 5.86 0 0 0 19.86.63C19.1.34 18.22.13 16.95.07 15.67.01 15.26 0 12 0Zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32Zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm6.41-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88Z" />
    </svg>
  );
}

function InfoRow({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-4">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white text-lp-green-dk">
        {icon}
      </div>
      <div className="flex-1 font-display text-lp-text-dk">{children}</div>
    </div>
  );
}